
import { getContainerPage, changeActiveContainer } from "@/service/container.service";
import { Container } from "@/types/container.type";
import { ElRowClassNameParams } from "@/types/element.type";
import { Action, ElMessage, ElMessageBox } from "element-plus";
import { defineComponent } from "vue";
import { debounce } from "lodash";
import PageHeaderComponent from "../PageHeader.component.vue";
import ContainerFormComponent from "./ContainerForm.component.vue";
import ContainerInfoComponent from "./ContainerInfo.component.vue";
import { Filter } from "@/types/filter.type";

export default defineComponent({
  components: { PageHeaderComponent, ContainerFormComponent, ContainerInfoComponent },
  props: {
    canWrite: {
      type: Boolean,
      default: false,
    },
    detailRouterName: {
      type: String,
    },
  },
  data() {
    return {
      containers: [] as Container[],
      currentPage: 0 as number,
      rowPerPage: 10 as number,
      totalRows: 0 as number,
      query: "" as string,
      showContainerForm: false,
      showContainerInfo: false,
      containerSelected: undefined as undefined | Container,
    };
  },
  created() {
    // - apply delay
    this.search = debounce(this.refreshContainer, 300);
  },
  mounted() {
    this.currentPage = 1;
    this.refreshContainer();
  },
  methods: {
    search(): void {
      this.refreshContainer();
    },
    /**
     * table row css class name
     * @param {ElRowClassNameParams<Container>} params
     * @return {string} css class name
     */
    tableRowClassName(params: ElRowClassNameParams<Container>): string {
      return params.row && params.row.active ? "" : "disable-row";
    },

    /**
     * set page
     * @param {number} page
     */
    changePaginationSize(size: number): void {
      this.rowPerPage = size;
      this.refreshContainer();
    },

    /**
     * change page
     * @param {number} page
     */
    changePaginationPage(page: number): void {
      this.currentPage = page;
      this.refreshContainer();
    },

    /**
     * confirm activate item
     * @param {Container} item
     */
    confirmActivateItem(item: Container) {
      ElMessageBox.confirm(
        this.$t("confirm.activateContainer", { name: item.name }),
        this.$t("label.warning"),
        {
          confirmButtonText: this.$t("btn.accept"),
          cancelButtonText: this.$t("btn.cancel"),
          type: "warning",
          dangerouslyUseHTMLString: true,
          callback: (action: Action) => {
            if (action === "confirm") {
              this.changeStatusContainer(item, true);
            }
          },
        }
      );
    },

    /**
     * confirm deactivate action
     * @param {Container} item
     */
    confirmDeactivateItem(item: Container) {
      ElMessageBox.confirm(
        this.$t("confirm.deactivateContainer", { name: item.name }),
        this.$t("label.warning"),
        {
          confirmButtonText: this.$t("btn.accept"),
          cancelButtonText: this.$t("btn.cancel"),
          type: "warning",
          dangerouslyUseHTMLString: true,
          callback: (action: Action) => {
            if (action === "confirm") {
              this.changeStatusContainer(item, false);
            }
          },
        }
      );
    },

    /**
     * change status container
     * @param {Container} item
     * @param {boolean} active
     */
    async changeStatusContainer(item: Container, active: boolean) {
      if (!item.id) {
        return;
      }

      // TODO el servicio debe retornar el item
      await changeActiveContainer(item.id);
      ElMessage.success(this.$t("success.action"));

      // - refrehs item
      this.refreshContainer({ ...item, active });
    },

    /**
     * refresh container item from list items
     * @param item
     */
    async refreshContainer(item: Container | undefined = undefined) {
      let index = -1;
      if (this.containers && item) {
        index = this.containers.findIndex((i) => i.id === item.id);
      }
      if (index !== -1 && item) {
        this.containers[index] = item;
      } else {
        const filter: Filter = {};
        if (this.query) {
          filter.multi = this.query;
        }
        const pagination = await getContainerPage(this.currentPage, this.rowPerPage, filter);
        this.totalRows = pagination.totalRows;
        this.containers = pagination.data;
      }
    },

    /**
     * open container formDialog
     * @param item
     */
    openContainerFormDialog(item: Container | undefined = undefined) {
      this.containerSelected = item;
      this.showContainerForm = true;
    },

    /**
     * open container infoDialog
     * @param item
     */
    openContainerInfoDialog(item: Container | undefined = undefined) {
      if (item && this.$route.path.includes("support")) {
        if (this.$route.path.includes("containers")) {
          this.$router.push("containers/" + item?.id);
        } else {
          this.$router.push("/support/containers/" + item?.id);
        }
      } else if (item?.id && this.detailRouterName) {
        this.$router.push({ name: this.detailRouterName, params: { containerid: item.id } });
      } else {
        this.containerSelected = item;
        this.showContainerInfo = true;
      }
    },

    /**
     * close container formDialog
     */
    closeContainerDialog() {
      this.showContainerForm = false;
      this.showContainerInfo = false;
      this.containerSelected = undefined;
    },

    /**
     * save container
     */
    async saveContainer() {
      // - call containerForm save method
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const container = await (
        this.$refs["containerForm"] as InstanceType<typeof ContainerFormComponent>
      ).doSave();

      // - close dialog and refresh
      this.closeContainerDialog();
      this.refreshContainer();
      ElMessage.success(this.$t("success.action"));
    },
  },
});
